/**
 * Landmark Detection Utilities
 *
 * Port of landmark detection logic from frontend-annotation test project.
 * Automatically detects pose landmarks within drawn annotation boundaries.
 */

import { LANDMARK_CONFIG } from './constants';

export interface Coordinates {
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  x2?: number;
  y2?: number;
}

export interface PoseLandmark {
  x: number;
  y: number;
  z?: number;
  visibility: number;
}

export interface PoseData {
  landmarks: PoseLandmark[];
}

/**
 * MediaPipe Pose landmark names mapping
 * Index -> Landmark name
 */
export const LANDMARK_NAMES: { [key: number]: string } = {
  0: 'nose',
  1: 'left_eye_inner',
  2: 'left_eye',
  3: 'left_eye_outer',
  4: 'right_eye_inner',
  5: 'right_eye',
  6: 'right_eye_outer',
  7: 'left_ear',
  8: 'right_ear',
  9: 'mouth_left',
  10: 'mouth_right',
  11: 'left_shoulder',
  12: 'right_shoulder',
  13: 'left_elbow',
  14: 'right_elbow',
  15: 'left_wrist',
  16: 'right_wrist',
  17: 'left_pinky',
  18: 'right_pinky',
  19: 'left_index',
  20: 'right_index',
  21: 'left_thumb',
  22: 'right_thumb',
  23: 'left_hip',
  24: 'right_hip',
  25: 'left_knee',
  26: 'right_knee',
  27: 'left_ankle',
  28: 'right_ankle',
  29: 'left_heel',
  30: 'right_heel',
  31: 'left_foot_index',
  32: 'right_foot_index',
};

/**
 * Find landmarks within drawn annotation bounds
 *
 * Detects which pose landmarks fall inside the drawn shape (rectangle, circle, or line).
 * Only includes landmarks with visibility > 0.5.
 *
 * @param coordinates - Shape coordinates
 * @param shape - Shape type
 * @param poseData - Pose data with landmarks
 * @param videoWidth - Video width for coordinate scaling
 * @param videoHeight - Video height for coordinate scaling
 * @returns Array of landmark indices that fall within bounds
 */
export function findLandmarksInBounds(
  coordinates: Coordinates,
  shape: 'rectangle' | 'circle' | 'line',
  poseData: PoseData,
  videoWidth: number,
  videoHeight: number
): number[] {
  const landmarkIndices: number[] = [];

  poseData.landmarks.forEach((landmark, index) => {
    // Convert normalized coordinates (0-1) to pixel coordinates
    const x = landmark.x * videoWidth;
    const y = landmark.y * videoHeight;

    let isInside = false;

    if (shape === 'rectangle' && coordinates.width && coordinates.height) {
      isInside =
        x >= coordinates.x &&
        x <= coordinates.x + coordinates.width &&
        y >= coordinates.y &&
        y <= coordinates.y + coordinates.height;
    } else if (shape === 'circle' && coordinates.radius) {
      const dx = x - coordinates.x;
      const dy = y - coordinates.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      isInside = distance <= coordinates.radius;
    } else if (shape === 'line' && coordinates.x2 !== undefined && coordinates.y2 !== undefined) {
      // For line, check if landmark is close to the line
      const distance = pointToLineDistance(
        x,
        y,
        coordinates.x,
        coordinates.y,
        coordinates.x2,
        coordinates.y2
      );
      isInside = distance <= LANDMARK_CONFIG.LINE_PROXIMITY_THRESHOLD;
    }

    // Only include landmarks with good visibility
    if (isInside && landmark.visibility > 0.5) {
      landmarkIndices.push(index);
    }
  });

  return landmarkIndices;
}

/**
 * Calculate distance from a point to a line segment
 *
 * @param px - Point x coordinate
 * @param py - Point y coordinate
 * @param x1 - Line start x
 * @param y1 - Line start y
 * @param x2 - Line end x
 * @param y2 - Line end y
 * @returns Distance in pixels
 */
function pointToLineDistance(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx: number;
  let yy: number;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = px - xx;
  const dy = py - yy;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get landmark names from indices
 *
 * @param indices - Array of landmark indices
 * @returns Array of landmark names
 */
export function getLandmarkNames(indices: number[]): string[] {
  return indices.map((i) => LANDMARK_NAMES[i] || `landmark_${i}`);
}

/**
 * Get landmark index from name
 *
 * @param name - Landmark name
 * @returns Landmark index or -1 if not found
 */
export function getLandmarkIndex(name: string): number {
  const entries = Object.entries(LANDMARK_NAMES);
  const found = entries.find(([_, landmarkName]) => landmarkName === name);
  return found ? parseInt(found[0], 10) : -1;
}

/**
 * Check if a landmark is visible in the pose data
 *
 * @param poseData - Pose data
 * @param landmarkIndex - Index of landmark to check
 * @param minVisibility - Minimum visibility threshold (default 0.5)
 * @returns true if landmark is visible
 */
export function isLandmarkVisible(
  poseData: PoseData,
  landmarkIndex: number,
  minVisibility: number = 0.5
): boolean {
  const landmark = poseData.landmarks[landmarkIndex];
  return landmark ? landmark.visibility >= minVisibility : false;
}

/**
 * Get all visible landmarks from pose data
 *
 * @param poseData - Pose data
 * @param minVisibility - Minimum visibility threshold (default 0.5)
 * @returns Array of visible landmark indices
 */
export function getVisibleLandmarks(
  poseData: PoseData,
  minVisibility: number = 0.5
): number[] {
  return poseData.landmarks
    .map((landmark, index) => (landmark.visibility >= minVisibility ? index : -1))
    .filter((index) => index !== -1);
}

/**
 * Result from finding the nearest landmark to a point
 */
export interface NearestLandmarkResult {
  index: number;
  name: string;
  distance: number;
  visibility: number;
  x: number;       // Pixel x coordinate
  y: number;       // Pixel y coordinate
  normalizedX: number;  // 0-1 normalized x
  normalizedY: number;  // 0-1 normalized y
}

/**
 * Find the nearest visible landmark to a click point
 *
 * Used for "snap to landmark" behavior in visual landmark selection.
 * Only considers landmarks with visibility above threshold.
 *
 * @param clickX - Click x coordinate (in pixels, relative to video)
 * @param clickY - Click y coordinate (in pixels, relative to video)
 * @param poseData - Pose data with landmarks
 * @param videoWidth - Video width for coordinate scaling
 * @param videoHeight - Video height for coordinate scaling
 * @param maxDistance - Maximum distance in pixels to consider (default 40px)
 * @param minVisibility - Minimum visibility threshold (default 0.3)
 * @returns Nearest landmark or null if none within maxDistance
 */
export function findNearestLandmark(
  clickX: number,
  clickY: number,
  poseData: PoseData,
  videoWidth: number,
  videoHeight: number,
  maxDistance: number = 40,
  minVisibility: number = 0.3
): NearestLandmarkResult | null {
  let nearest: NearestLandmarkResult | null = null;
  let nearestDistance = Infinity;

  poseData.landmarks.forEach((landmark, index) => {
    // Skip landmarks with low visibility
    if (landmark.visibility < minVisibility) return;

    // Convert normalized coordinates (0-1) to pixel coordinates
    const x = landmark.x * videoWidth;
    const y = landmark.y * videoHeight;

    // Calculate distance
    const dx = clickX - x;
    const dy = clickY - y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Update nearest if closer and within max distance
    if (distance < nearestDistance && distance <= maxDistance) {
      nearestDistance = distance;
      nearest = {
        index,
        name: LANDMARK_NAMES[index] || `landmark_${index}`,
        distance,
        visibility: landmark.visibility,
        x,
        y,
        normalizedX: landmark.x,
        normalizedY: landmark.y,
      };
    }
  });

  return nearest;
}

/**
 * Get all landmarks with their screen positions and visibility info
 *
 * Used for rendering landmark dots on canvas overlay.
 *
 * @param poseData - Pose data with landmarks
 * @param videoWidth - Video width for coordinate scaling
 * @param videoHeight - Video height for coordinate scaling
 * @returns Array of landmark info with positions and visibility
 */
export interface LandmarkPosition {
  index: number;
  name: string;
  x: number;           // Pixel x coordinate
  y: number;           // Pixel y coordinate
  visibility: number;
  visibilityCategory: 'high' | 'medium' | 'low';  // For color coding
}

export function getAllLandmarkPositions(
  poseData: PoseData,
  videoWidth: number,
  videoHeight: number
): LandmarkPosition[] {
  return poseData.landmarks.map((landmark, index) => {
    const x = landmark.x * videoWidth;
    const y = landmark.y * videoHeight;

    // Categorize visibility for color coding
    let visibilityCategory: 'high' | 'medium' | 'low';
    if (landmark.visibility >= 0.8) {
      visibilityCategory = 'high';
    } else if (landmark.visibility >= 0.5) {
      visibilityCategory = 'medium';
    } else {
      visibilityCategory = 'low';
    }

    return {
      index,
      name: LANDMARK_NAMES[index] || `landmark_${index}`,
      x,
      y,
      visibility: landmark.visibility,
      visibilityCategory,
    };
  });
}

/**
 * Human-readable label for a landmark name
 *
 * Converts snake_case landmark names to Title Case labels.
 * Handles functional landmarks (lead_/trail_ prefix) specially:
 *   e.g., "lead_wrist" -> "Lead Wrist"
 *   e.g., "trail_knee" -> "Trail Knee"
 * Also handles anatomical landmarks:
 *   e.g., "left_shoulder" -> "Left Shoulder"
 *
 * @param name - Landmark name in snake_case (anatomical or functional)
 * @returns Human-readable label
 */
export function getLandmarkLabel(name: string): string {
  // Handle functional landmarks (lead_/trail_ prefix)
  if (name.startsWith('lead_') || name.startsWith('trail_')) {
    const prefix = name.startsWith('lead_') ? 'Lead' : 'Trail';
    const bodyPart = name.replace(/^(lead_|trail_)/, '');
    return `${prefix} ${bodyPart.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`;
  }

  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
